import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { SectionHeader } from '../ui/SectionHeader';
import { SEGMENT_ITEMS } from '../../lib/constants';
import styles from './SegmentsSection.module.css';

const SEGMENT_ICONS: Record<string, React.ReactNode> = {
  medtech: (
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  manufacturing: (
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  ),
  it: (
    <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
};

const SEGMENT_ICON_CLASSES: Record<string, string> = {
  medtech: styles.iconMedtech,
  manufacturing: styles.iconManuf,
  it: styles.iconIt,
};

const SEGMENT_CARD_CLASSES: Record<string, string> = {
  medtech: styles.cardMedtech,
  manufacturing: styles.cardManufacturing,
  it: styles.cardIt,
};

export const SegmentsSection = () => {
  const { t, i18n } = useTranslation();
  const [ref, visible] = useScrollReveal(0.1);
  const lang = (i18n.language === 'en' ? 'en' : 'it') as 'it' | 'en';

  return (
    <section id="segments" className={styles.section} ref={ref as React.RefObject<HTMLElement>}>
      <div className="container">
        <SectionHeader
          label={t('segments.label')}
          title={t('segments.title')}
        />

        <div className={styles.grid}>
          {SEGMENT_ITEMS.map((seg, idx) => (
            <Link
              key={seg.id}
              to={`/segments/${seg.id}`}
              className={`${styles.card} ${SEGMENT_CARD_CLASSES[seg.id]} ${visible ? styles.cardVisible : ''}`}
              style={{ transitionDelay: `${idx * 150}ms` }}
            >
              {/* Telemetry Railway Gutter */}
              <div className={styles.cardTrackContainer}>
                <svg className={styles.cardTrackSvg} viewBox="0 0 20 200" preserveAspectRatio="none">
                  {/* Two rails */}
                  <line x1="6" y1="0" x2="6" y2="200" className={styles.railLine} />
                  <line x1="14" y1="0" x2="14" y2="200" className={styles.railLine} />
                  {/* Ties */}
                  <line x1="3" y1="20" x2="17" y2="20" className={styles.railTie} />
                  <line x1="3" y1="40" x2="17" y2="40" className={styles.railTie} />
                  <line x1="3" y1="60" x2="17" y2="60" className={styles.railTie} />
                  <line x1="3" y1="80" x2="17" y2="80" className={styles.railTie} />
                  <line x1="3" y1="100" x2="17" y2="100" className={styles.railTie} />
                  <line x1="3" y1="120" x2="17" y2="120" className={styles.railTie} />
                  <line x1="3" y1="140" x2="17" y2="140" className={styles.railTie} />
                  <line x1="3" y1="160" x2="17" y2="160" className={styles.railTie} />
                  <line x1="3" y1="180" x2="17" y2="180" className={styles.railTie} />
                  
                  {/* Glowing telemetry path */}
                  <path d="M 10 0 L 10 200" className={styles.signalLine} />
                </svg>
              </div>

              <div className={styles.cardContent}>
                <div className={`${styles.cardIcon} ${SEGMENT_ICON_CLASSES[seg.id]}`}>
                  {SEGMENT_ICONS[seg.id]}
                </div>
                <h3 className={styles.cardTitle}>{seg.title[lang]}</h3>
                <p className={styles.cardDesc}>{seg.desc[lang]}</p>
                
                <div className={styles.pain}>
                  <svg className={styles.painIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className={styles.painText}>{seg.pain[lang]}</span>
                </div>
              </div>

              <div className={styles.learnMore}>
                <span>{t('segments.learn_more')}</span>
                <svg className={styles.arrowIcon} width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L19 10m0 0l-5.5 5.5M19 10H5" />
                </svg>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
